import {Skeleton, type TableColumnsType, type TableColumnType, type TableProps} from "antd";
import type {ColumnGroupType} from "antd/es/table";

/** Marker type for skeleton/loading rows */
export type SkeletonRow = { __skeleton__: true; key: React.Key };

/**
 * Column definition
 */
export type ColumnWithKey<T> =
    | (TableColumnType<T> & { renderSkeleton?: React.ReactNode })
    | (ColumnGroupType<T> & { children: ColumnsWithKey<T> });

export type ColumnsWithKey<T> = ColumnWithKey<T>[];

/** Helper function: Build skeleton rows for loading state */
export function makeSkeletonRows<T>(count: number): (T & SkeletonRow)[] {
  return Array.from({length: count}, (_, i) => ({
    key: `__skeleton__-${i}`,
    __skeleton__: true as const,
  })) as (T & SkeletonRow)[];
}

/** Type guards – without using any */
function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * Type guard to check if a value is a skeleton row.
 * Used to distinguish loading placeholders from actual data rows.
 */
export function isSkeletonRow(v: unknown): v is SkeletonRow {
  return isObject(v) && "__skeleton__" in v;
}

function isColumnGroup<T>(
    c: ColumnWithKey<T>
): c is ColumnGroupType<T> & { children: ColumnsWithKey<T> } {
  return "children" in c && Array.isArray((c as { children?: unknown }).children);
}

/**
 * Recursively wraps columns and enables skeleton rendering for loading states.
 * When loading is true, skeleton rows will use the custom renderSkeleton or a default skeleton.
 * Column groups are handled recursively to support nested column structures.
 */
export function applyColumnSkeletons<T>(
    columns: ColumnsWithKey<T>,
    loading: boolean
): TableColumnsType<T> {
  return columns.map((c) => {
    // Handle column groups recursively
    if (isColumnGroup<T>(c)) {
      const {children, ...groupRest} = c;
      return {
        ...groupRest,
        children: applyColumnSkeletons<T>(children, loading),
      };
    }

    const col = c as TableColumnType<T> & { renderSkeleton?: React.ReactNode; key?: React.Key };
    const {renderSkeleton, render: origRender, key, ...rest} = col;

    // Use custom skeleton renderer or default skeleton button
    const skeletonNode =
        renderSkeleton ?? (
            <div className={"w-full"}>
              <Skeleton.Button active size="small" style={{width: 70, height: 22}}/>
            </div>
        );

    // Important: Signature from AntD, but without explicit `any` in our code.
    type RenderFn = NonNullable<TableColumnType<T>["render"]>;
    const wrappedRender: RenderFn = (value, record, index) => {
      // Show skeleton for loading state, otherwise use original render or value
      if (loading && isSkeletonRow(record)) return skeletonNode;
      return origRender ? origRender(value, record, index) : (value as React.ReactNode);
    };

    // Do NOT return `renderSkeleton` → return proper ColumnType<T>
    const result: TableColumnType<T> = {
      ...rest,
      key,
      render: wrappedRender,
    };
    return result;
  });
}

/**
 * Builds the display data source for the table.
 * When loading, returns skeleton rows instead of actual data.
 * Uses a single cast to maintain type safety while allowing runtime skeleton markers.
 */
export function buildDisplayData<T>(
    data: T[],
    loading: boolean,
    skeletonCount: number
): T[] {
  if (!loading) return data;
  const sk = makeSkeletonRows<T>(skeletonCount);
  // For AntD, the dataset remains type T; at runtime it contains skeleton markers.
  return sk as unknown as T[];
}

/**
 * Wraps the onRow callback to handle row selection when enableRowSelection is true.
 * When enabled, selects the row first, then calls the original onClick handler.
 */
export function wrapOnRowWithSelection<T extends object>(
    onRowCallback: TableProps<T>["onRow"],
    enableRowSelection: boolean,
    getRowKey: (record: T) => React.Key,
    setSelectedRowKey: (key: React.Key | null) => void
): TableProps<T>["onRow"] {
  if (!enableRowSelection) {
    // Selection disabled: just pass through the original onRow (or undefined)
    return onRowCallback;
  }

  // Selection enabled: wrap to select first, then call original handler
  return (record: T, index?: number) => {
    // Call the original onRow handler first to get its result
    const originalResult = onRowCallback ? onRowCallback(record, index) : undefined;
    
    // Get the record key for selection
    const recordKey = getRowKey(record);
    
    // If original handler returns an object with onClick, wrap it
    if (originalResult && typeof originalResult === "object" && "onClick" in originalResult) {
      const originalOnClick = originalResult.onClick;
      return {
        ...originalResult,
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          // First: select the row
          setSelectedRowKey(recordKey);
          // Then: call the original onClick handler
          if (originalOnClick) {
            originalOnClick(event);
          }
        },
      };
    }

    // If no original handler or it doesn't return onClick, add our own onClick for selection
    return {
      ...originalResult,
      onClick: () => {
        setSelectedRowKey(recordKey);
      },
    };
  };
}